// Collection API service — ported from public/collection-api.js
// Handles collection pagination, prefetching, gap-fill, and staleness detection

import { getFoilTypeClass } from '../logic/foil-utils.js';
import { determineCurrencyFromLanguage } from '../logic/utils.js';
import {
  getPageCacheKey,
  getCachedCollectionPage,
  cacheCollectionPage,
  getCacheKey,
  getCollectionMeta,
  updateCollectionMeta,
  clearCollectionPageCache,
  isCacheAvailable,
} from './indexeddb-cache.js';
import { API_ENDPOINTS } from './endpoints.js';
import { apiFetch } from './api-fetch.js';

const PAGE_SIZE = 252; // Multiple of 9 (28 binder pages per fetch)

// Promise that resolves when stale IndexedDB page cache has been cleared.
let pendingCacheClear = null;

/**
 * Creates a placeholder object for unfetched cards
 */
export function createPlaceholder(index) {
  return { status: 'placeholder', index };
}

/**
 * Generates a cache key for a card
 */
export function getCardCacheKey(card) {
  const id = card.cardData?.id || card.id;
  const isFoil = card.isFoil || card.is_foil || false;
  const lang = card.lang || card.cardData?.lang || 'en';
  const condition = card.condition || card.cardData?.condition || '';
  return `${id}_${isFoil}_${lang}_${condition}`;
}

/**
 * Processes a raw card from the API into the format expected by the UI
 * @param {Object} card - Raw card from API
 * @param {string} selectedCurrency - 'usd' or 'eur'
 * @returns {Object} Processed card object
 */
export function processCard(card, selectedCurrency) {
  if (card.not_found) {
    return {
      status: 'not_found',
      identifier: { set: card.set, collector_number: card.collector_number },
      isFoil: card.is_foil || false,
      availableCount: card.quantity || 1,
      selectedCount: 0,
      originalLineRef: `${card.name || 'Unknown'} (${card.set?.toUpperCase()}) ${card.collector_number}`,
      cardData: null,
    };
  }

  const isFoil = card.is_foil || false;
  const finishType = card.finish_type;
  const currency = selectedCurrency || determineCurrencyFromLanguage();
  const price = parseFloat(currency === 'usd' ? card.price_usd : card.price_eur) || 0;
  const foilTypeClass = getFoilTypeClass(card);

  return {
    status: 'found',
    identifier: { set: card.set, collector_number: card.collector_number },
    isFoil: isFoil,
    finishType: finishType,
    foilTypeClass: foilTypeClass,
    availableCount: card.quantity || 1,
    selectedCount: 0,
    originalLineRef: `${card.name} (${card.set?.toUpperCase()}) ${card.collector_number}${isFoil ? ' *F*' : ''}`,
    cardData: card,
    price: price,
    priceCurrency: currency,
    lang: card.lang || 'en',
    condition: card.condition || '',
  };
}

/**
 * Adds cards to a cache Map and returns processed card references
 * @param {Array} cards - Raw cards from API
 * @param {Map} cardCache - The card cache Map
 * @param {string} selectedCurrency - 'usd' or 'eur'
 * @returns {Array} Array of processed card objects
 */
export function cacheCards(cards, cardCache, selectedCurrency) {
  const refs = [];
  for (const card of cards) {
    const processed = processCard(card, selectedCurrency);
    const key = getCardCacheKey(processed);

    if (!cardCache.has(key)) {
      cardCache.set(key, processed);
    }
    refs.push(cardCache.get(key));
  }
  return refs;
}

/**
 * Fetches a page of collection cards from the backend.
 * Supports both Archidekt and Moxfield collections via unified V5 endpoint.
 * Uses IndexedDB cache with ETag validation.
 *
 * @param {Object} collectionInfo - Collection metadata
 * @param {string} collectionInfo.source - 'moxfield' | 'archidekt'
 * @param {string} collectionInfo.sourceId - The collection ID
 * @param {string} collectionInfo.collectionType - 'collection' | 'binder' | 'deck'
 * @param {string} collectionInfo.selectedCurrency - 'usd' | 'eur'
 * @param {string} sortMode - 'price' or 'color'
 * @param {string|null} colorFilter - Color code for color sort
 * @param {number} page - Page number (1-indexed)
 * @param {string|null} sessionId - Optional pagination token
 * @returns {Promise<Object>} Response data
 */
export async function fetchCollectionPage(
  collectionInfo,
  sortMode,
  colorFilter,
  page,
  sessionId = null
) {
  const endpoint = API_ENDPOINTS.get_binder_page_v5;
  const { source, sourceId, collectionType, selectedCurrency } = collectionInfo;

  console.log(
    `[Paginated Load V5] Fetching page ${page} for ${collectionType} (${source}), ` +
      `sort=${sortMode}, color=${colorFilter || 'all'}${sessionId ? ', with sessionId' : ''}`
  );

  // Wait for pending cache clear
  if (pendingCacheClear) {
    await pendingCacheClear;
    pendingCacheClear = null;
  }

  // Check IndexedDB cache
  let cached = null;
  let pageKey = null;
  let collectionKey = null;

  if (isCacheAvailable()) {
    pageKey = getPageCacheKey(source, collectionType, sourceId, sortMode, colorFilter, page);
    collectionKey = getCacheKey(source, collectionType, sourceId);
    cached = await getCachedCollectionPage(pageKey);

    if (cached && !cached.isStale) {
      console.log(`[Paginated Load] Fresh cache hit for page ${page}`);
      return cached.data;
    }

    if (cached) {
      console.log(`[Paginated Load] Stale cache found, validating with ETag`);
    }
  }

  /** @type {Record<string, string>} */
  const headers = {};
  if (cached?.etag) {
    headers['If-None-Match'] = cached.etag;
  }

  const requestBody = {
    source,
    sourceType: collectionType,
    sourceId,
    sortMode,
    colorFilter,
    page,
    pageSize: PAGE_SIZE,
    sessionId,
    currency: selectedCurrency,
  };

  // A public resource: no token (see binder-api.js loadCollectionV5).
  const response = await apiFetch(
    endpoint,
    { method: 'POST', headers, json: requestBody },
    { token: null }
  );

  if (response.status === 304 && cached) {
    console.log(`[Paginated Load] 304 Not Modified for page ${page}`);
    await cacheCollectionPage(pageKey, collectionKey, cached.data, cached.etag);
    return cached.data;
  }

  if (!response.ok) {
    if (cached) {
      console.warn(`[Paginated Load] Fetch failed, returning stale cache for page ${page}`);
      return cached.data;
    }
    const error = await response.json().catch(() => ({}));
    console.error(`[Collection API] HTTP error ${response.status}:`, error);
    throw new Error(error.error || `API error: ${response.status}`);
  }

  const result = await response.json();
  if (!result.success) {
    const errorMsg = result.sync?.error || result.error || 'Unknown error';
    console.error('[Collection API] API returned error:', errorMsg);
    throw new Error(errorMsg);
  }

  // Flatten the V5 bundled response (data / pagination / sync)
  const { data, pagination, sync } = result;
  const responseData = {
    cards: data?.cards || [],
    name: data?.name,
    owner: data?.owner,
    totalCards: pagination?.totalCards,
    page: pagination?.page,
    pageSize: pagination?.pageSize,
    totalPages: pagination?.totalPages,
    hasMore: pagination?.page < pagination?.totalPages,
    syncStatus: sync?.status,
    pageReady: sync?.pageReady,
    syncError: sync?.error,
    syncedAt: sync?.syncedAt,
    colorOffsets: result.colorOffsets || null,
  };

  // Cache response (only if page is ready and sync is complete)
  const etag = response.headers.get('ETag');
  if (
    pageKey &&
    collectionKey &&
    etag &&
    responseData.pageReady !== false &&
    responseData.syncStatus !== 'syncing'
  ) {
    cacheCollectionPage(pageKey, collectionKey, responseData, etag);
    updateCachedPagesMetadata(collectionKey, sortMode, colorFilter, page);
  }

  console.log(
    `[Paginated Load] Got ${responseData.cards?.length} cards, hasMore=${responseData.hasMore}`
  );
  return responseData;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Fetches a page with retry polling for COLD path sync.
 */
export async function fetchPageWithRetry(
  collectionInfo,
  sortMode,
  colorFilter,
  page,
  options = {}
) {
  const { maxRetries = 30, retryDelayMs = 1000 } = options;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const result = await fetchCollectionPage(collectionInfo, sortMode, colorFilter, page);

    if (result.syncStatus === 'failed') {
      throw new Error(result.syncError || 'Sync failed');
    }

    if (result.pageReady !== false) {
      return result;
    }

    console.log(
      `[Paginated Load] Page ${page} not ready, retrying in ${retryDelayMs}ms (attempt ${attempt + 1}/${maxRetries})`
    );
    await sleep(retryDelayMs);
  }

  throw new Error(`Sync timeout: page ${page} not ready after ${maxRetries} retries`);
}

/**
 * Updates collection metadata to track which pages have been cached
 */
async function updateCachedPagesMetadata(collectionKey, sortMode, colorFilter, page) {
  try {
    const meta = (await getCollectionMeta(collectionKey)) || { cachedPages: {} };

    if (!meta.cachedPages) {
      meta.cachedPages = {
        price: [],
        color: { w: [], u: [], b: [], r: [], g: [], c: [], multi: [] },
      };
    }

    if (sortMode === 'price') {
      if (!Array.isArray(meta.cachedPages.price)) {
        meta.cachedPages.price = [];
      }
      if (!meta.cachedPages.price.includes(page)) {
        meta.cachedPages.price.push(page);
        meta.cachedPages.price.sort((a, b) => a - b);
      }
    } else if (colorFilter) {
      if (!meta.cachedPages.color) {
        meta.cachedPages.color = {};
      }
      if (!meta.cachedPages.color[colorFilter]) {
        meta.cachedPages.color[colorFilter] = [];
      }
      if (!meta.cachedPages.color[colorFilter].includes(page)) {
        meta.cachedPages.color[colorFilter].push(page);
        meta.cachedPages.color[colorFilter].sort((a, b) => a - b);
      }
    }

    await updateCollectionMeta(collectionKey, meta);
  } catch (error) {
    console.warn('[Collection API] Failed to update cached pages metadata:', error);
  }
}

/**
 * Initializes collection data with the initial load response.
 * Returns the initialized collection data object.
 *
 * @param {Object} params
 * @param {number} params.totalCards
 * @param {Array} params.initialPriceCards
 * @param {Array} params.initialColorCards
 * @param {boolean} params.priceHasMore
 * @param {boolean} params.colorHasMore
 * @param {string|null} params.syncedAt
 * @param {Map} params.cardCache - The card cache Map
 * @param {string} params.selectedCurrency
 * @param {string} params.source
 * @param {string} params.collectionType
 * @param {string} params.sourceId
 * @returns {Object} Initialized collection data
 */
export function initializeCollectionData(params) {
  const {
    totalCards,
    initialPriceCards,
    initialColorCards,
    priceHasMore,
    colorHasMore,
    syncedAt = null,
    cardCache,
    selectedCurrency,
    source,
    collectionType,
    sourceId,
  } = params;

  console.log('[Collection] initializeCollectionData called with:', {
    totalCards,
    initialPriceCardsCount: initialPriceCards?.length,
    initialColorCardsCount: initialColorCards?.length,
    priceHasMore,
    colorHasMore,
  });

  // Clear existing in-memory cache
  cardCache.clear();

  // Clear stale IndexedDB page cache
  if (isCacheAvailable() && source && sourceId) {
    const collectionKey = getCacheKey(source, collectionType || 'collection', sourceId);
    pendingCacheClear = clearCollectionPageCache(collectionKey);
  }

  // Cache initial cards and get references
  const priceRefs = cacheCards(initialPriceCards || [], cardCache, selectedCurrency);
  const colorRefs = cacheCards(initialColorCards || [], cardCache, selectedCurrency);

  // Initialize sort arrays: first page of real cards, rest placeholders
  const byPrice = [
    ...priceRefs,
    ...Array(Math.max(0, totalCards - priceRefs.length))
      .fill(null)
      .map((_, i) => createPlaceholder(priceRefs.length + i)),
  ];

  const byColor = [
    ...colorRefs,
    ...Array(Math.max(0, totalCards - colorRefs.length))
      .fill(null)
      .map((_, i) => createPlaceholder(colorRefs.length + i)),
  ];

  console.log('[Collection] Arrays initialized:', {
    byPriceLength: byPrice.length,
    byColorLength: byColor.length,
  });

  const collectionData = {
    totalCards,
    byPrice,
    byColor,
    priceLoadState: {
      page: 1,
      cardsLoaded: priceRefs.length,
      hasMore: priceHasMore,
      loading: false,
      sessionId: null,
    },
    colorLoadState: {
      page: 1,
      cardsLoaded: colorRefs.length,
      hasMore: colorHasMore,
      loading: false,
      sessionId: null,
    },
    pageVersions: { price: {}, color: {} },
    gapFetchLoading: { price: new Set(), color: new Set() },
    latestSyncedAt: syncedAt || null,
  };

  if (syncedAt) {
    collectionData.pageVersions.price[1] = syncedAt;
    collectionData.pageVersions.color[1] = syncedAt;
  }

  return collectionData;
}

/**
 * Creates a fresh/reset collection data object
 */
export function createEmptyCollectionData() {
  return {
    totalCards: 0,
    byPrice: [],
    byColor: [],
    priceLoadState: {
      page: 0,
      hasMore: true,
      loading: false,
      sessionId: null,
    },
    colorLoadState: {
      page: 0,
      hasMore: true,
      loading: false,
      sessionId: null,
    },
    latestSyncedAt: null,
    pageVersions: { price: {}, color: {} },
    gapFetchLoading: { price: new Set(), color: new Set() },
  };
}

// --- Staleness detection ---

function trackPageVersion(collectionData, sortMode, apiPage, syncedAt) {
  if (!syncedAt) return;
  const prev = collectionData.latestSyncedAt;
  collectionData.pageVersions[sortMode][apiPage] = syncedAt;
  if (syncedAt > (prev || '')) {
    if (prev && prev !== syncedAt) {
      console.log(`[Stale] syncedAt updated: ${prev} → ${syncedAt}`);
    }
    collectionData.latestSyncedAt = syncedAt;
  }
}

/**
 * Handles sync probe response. Returns true if newer syncedAt was detected.
 * @returns {boolean}
 */
function handleSyncProbe(collectionData, sortMode, data) {
  if (!data.syncedAt) return false;
  const prev = collectionData.latestSyncedAt;
  if (prev && data.syncedAt <= prev) return false;

  console.log(`[Stale] Sync complete detected via probe`);
  trackPageVersion(collectionData, sortMode, 1, data.syncedAt);

  // Re-enable price loading if stopped during syncing
  const priceLoadState = collectionData.priceLoadState;
  if (!priceLoadState.hasMore && priceLoadState.cardsLoaded < collectionData.byPrice.length) {
    priceLoadState.page = Math.floor((priceLoadState.cardsLoaded || 0) / PAGE_SIZE);
    priceLoadState.cardsLoaded = priceLoadState.page * PAGE_SIZE;
    priceLoadState.hasMore = true;
    priceLoadState.sessionId = null;
  }

  // Enable color sort loading (was skipped during COLD path)
  const colorLoadState = collectionData.colorLoadState;
  if (colorLoadState.cardsLoaded === 0) {
    colorLoadState.page = 0;
    colorLoadState.hasMore = true;
    colorLoadState.sessionId = null;
  }

  return true;
}

export function isPageStale(collectionData, sortMode, apiPage) {
  const pageVersion = collectionData.pageVersions[sortMode]?.[apiPage];
  const latest = collectionData.latestSyncedAt;
  if (!pageVersion || !latest) return false;
  return pageVersion < latest;
}

export function binderPageToApiPage(binderPageIndex, cardsPerPage) {
  const startCard = binderPageIndex * cardsPerPage;
  return Math.floor(startCard / PAGE_SIZE) + 1;
}

/**
 * Checks if binder page has an in-flight gap fetch.
 */
export function hasStaleGapAtPage(collectionData, sortMode, binderPageIndex, cardsPerPage) {
  const apiPage = binderPageToApiPage(binderPageIndex, cardsPerPage);
  return collectionData.gapFetchLoading[sortMode]?.has(apiPage) ?? false;
}

/**
 * Fetches fresh data for a stale API page and fills the sort array.
 * @returns {Promise<boolean>} True if a gap fetch was triggered
 */
export async function fetchAndFillGap(
  collectionInfo,
  collectionData,
  sortMode,
  binderPageIndex,
  cardsPerPage,
  cardCache
) {
  const apiPage = binderPageToApiPage(binderPageIndex, cardsPerPage);

  if (!isPageStale(collectionData, sortMode, apiPage)) return false;
  if (collectionData.gapFetchLoading[sortMode].has(apiPage)) return false;

  const sortArray = sortMode === 'price' ? collectionData.byPrice : collectionData.byColor;

  collectionData.gapFetchLoading[sortMode].add(apiPage);
  console.log(`[Stale] Re-fetching API page ${apiPage} for binder page ${binderPageIndex}`);
  try {
    const data = await fetchCollectionPage(collectionInfo, sortMode, null, apiPage);
    const cardRefs = cacheCards(data.cards, cardCache, collectionInfo.selectedCurrency);

    const rangeStart = (apiPage - 1) * PAGE_SIZE;
    for (let i = 0; i < cardRefs.length; i++) {
      if (rangeStart + i < sortArray.length) {
        sortArray[rangeStart + i] = cardRefs[i];
      }
    }

    trackPageVersion(collectionData, sortMode, apiPage, data.syncedAt);

    const latest = collectionData.latestSyncedAt;
    if (latest && (!data.syncedAt || data.syncedAt < latest)) {
      collectionData.pageVersions[sortMode][apiPage] = latest;
    }

    return true;
  } catch (err) {
    console.error(`[Stale] Error re-fetching API page ${apiPage}:`, err);
    return false;
  } finally {
    collectionData.gapFetchLoading[sortMode].delete(apiPage);
  }
}

/**
 * Load color-sorted cards until the target card index is covered.
 * Used by tab jump navigation to ensure destination cards exist before flipping.
 * @returns {Promise<boolean>} True when target index is loaded
 */
export async function loadColorCardsUpTo(
  targetCardIndex,
  collectionInfo,
  collectionData,
  binderSyncStatus,
  cardCache
) {
  const loadState = collectionData?.colorLoadState;
  if (!loadState) return false;

  // Keep loading pages until we've covered the target index
  const maxAttempts = 20; // safety limit
  for (let i = 0; i < maxAttempts; i++) {
    const loaded = loadState.cardsLoaded || 0;
    if (loaded > targetCardIndex || !loadState.hasMore) break;

    await loadMoreCards(collectionInfo, collectionData, 'color', binderSyncStatus, cardCache);
  }
  return true;
}

/**
 * @typedef {{ loaded: boolean, syncLanded: boolean }} LoadMoreResult
 * `loaded`: the prefetch may chain (false: skipped, already loading or no
 * more pages). `syncLanded`: a sync probe saw a newer sync, so a COLD
 * binder's items now exist (the caller refetches the star's want hits).
 */
/** @type {LoadMoreResult} */
const LOADED = Object.freeze({ loaded: true, syncLanded: false });
/** @type {LoadMoreResult} */
const SYNC_LANDED = Object.freeze({ loaded: true, syncLanded: true });
/** @type {LoadMoreResult} */
const NOTHING_LOADED = Object.freeze({ loaded: false, syncLanded: false });

/**
 * Loads the next page of cards for `sortMode`.
 * @returns {Promise<LoadMoreResult>}
 */
export async function loadMoreCards(
  collectionInfo,
  collectionData,
  sortMode,
  binderSyncStatus,
  cardCache
) {
  if (sortMode === 'price') {
    return loadMorePriceCards(collectionInfo, collectionData, binderSyncStatus, cardCache);
  } else {
    return loadMoreColorCards(collectionInfo, collectionData, binderSyncStatus, cardCache);
  }
}

async function loadMorePriceCards(collectionInfo, collectionData, binderSyncStatus, cardCache) {
  const loadState = collectionData.priceLoadState;

  if (!loadState.hasMore) {
    if (binderSyncStatus === 'syncing' && !loadState.loading) {
      loadState.loading = true;
      try {
        const data = await fetchCollectionPage(collectionInfo, 'price', null, 1);
        if (handleSyncProbe(collectionData, 'price', data)) return SYNC_LANDED;
      } finally {
        loadState.loading = false;
      }
    }
    return NOTHING_LOADED;
  }
  if (loadState.loading) return NOTHING_LOADED;

  loadState.loading = true;
  const nextPage = loadState.page + 1;

  try {
    const data = await fetchPageWithRetry(collectionInfo, 'price', null, nextPage);

    if (handleSyncProbe(collectionData, 'price', data)) return SYNC_LANDED;

    const cardRefs = cacheCards(data.cards, cardCache, collectionInfo.selectedCurrency);

    const startIndex = loadState.cardsLoaded || loadState.page * PAGE_SIZE;
    for (let i = 0; i < cardRefs.length; i++) {
      if (startIndex + i < collectionData.byPrice.length) {
        collectionData.byPrice[startIndex + i] = cardRefs[i];
      }
    }

    loadState.page = nextPage;
    loadState.cardsLoaded = (loadState.cardsLoaded || 0) + cardRefs.length;
    loadState.hasMore = data.hasMore;
    if (data.sessionId) {
      loadState.sessionId = data.sessionId;
    }

    trackPageVersion(collectionData, 'price', nextPage, data.syncedAt);

    // Trim orphan placeholders when all data loaded
    if (
      !data.hasMore &&
      loadState.cardsLoaded < collectionData.byPrice.length &&
      binderSyncStatus !== 'syncing'
    ) {
      collectionData.byPrice.length = loadState.cardsLoaded;
    }

    return LOADED;
  } catch (err) {
    console.error(`[Collection] Error loading price page ${nextPage}:`, err);
    throw err;
  } finally {
    loadState.loading = false;
  }
}

async function loadMoreColorCards(collectionInfo, collectionData, binderSyncStatus, cardCache) {
  const loadState = collectionData.colorLoadState;

  if (!loadState.hasMore) {
    if (binderSyncStatus === 'syncing' && !loadState.loading) {
      loadState.loading = true;
      try {
        const data = await fetchCollectionPage(collectionInfo, 'color', null, 1);
        if (handleSyncProbe(collectionData, 'color', data)) return SYNC_LANDED;
      } finally {
        loadState.loading = false;
      }
    }
    return NOTHING_LOADED;
  }
  if (loadState.loading) return NOTHING_LOADED;

  loadState.loading = true;
  const nextPage = loadState.page + 1;

  try {
    const data = await fetchPageWithRetry(collectionInfo, 'color', null, nextPage);

    if (handleSyncProbe(collectionData, 'color', data)) return SYNC_LANDED;

    const cardRefs = cacheCards(data.cards, cardCache, collectionInfo.selectedCurrency);

    const startIndex = loadState.cardsLoaded || loadState.page * PAGE_SIZE;
    for (let i = 0; i < cardRefs.length; i++) {
      if (startIndex + i < collectionData.byColor.length) {
        collectionData.byColor[startIndex + i] = cardRefs[i];
      }
    }

    loadState.page = nextPage;
    loadState.cardsLoaded = (loadState.cardsLoaded || 0) + cardRefs.length;
    loadState.hasMore = data.hasMore;
    if (data.sessionId) {
      loadState.sessionId = data.sessionId;
    }

    trackPageVersion(collectionData, 'color', nextPage, data.syncedAt);

    if (
      !data.hasMore &&
      loadState.cardsLoaded < collectionData.byColor.length &&
      binderSyncStatus !== 'syncing'
    ) {
      collectionData.byColor.length = loadState.cardsLoaded;
    }

    return LOADED;
  } catch (err) {
    console.error(`[Collection] Error loading color page ${nextPage}:`, err);
    throw err;
  } finally {
    loadState.loading = false;
  }
}

/**
 * Checks if prefetch is needed based on current page position
 */
export function shouldPrefetch(
  currentPage,
  cardsPerPage,
  bufferSize,
  isCollection,
  currentSortMode,
  collectionData,
  binderSyncStatus
) {
  if (!isCollection) return false;

  const loadState =
    currentSortMode === 'price' ? collectionData.priceLoadState : collectionData.colorLoadState;

  if (loadState.loading) return false;

  const prefetchThreshold = 30;
  const checkIndex = (currentPage + bufferSize + prefetchThreshold) * cardsPerPage;
  const cardsLoaded = loadState.cardsLoaded || 0;
  const hasMoreToLoad = loadState.hasMore;

  if (checkIndex >= cardsLoaded && hasMoreToLoad) return true;
  if (!hasMoreToLoad && binderSyncStatus === 'syncing') return true;

  return false;
}

/**
 * Gets the card array for the current sort mode
 */
export function getCurrentSortArray(isCollection, currentSortMode, collectionData, allCardsData) {
  if (!isCollection) return allCardsData;
  return currentSortMode === 'price' ? collectionData.byPrice : collectionData.byColor;
}

/**
 * Checks if the next page past the buffer would contain placeholders
 */
export function hasPlaceholdersPastBuffer(
  currentLocation,
  cardsPerPage,
  bufferSize,
  isCollection,
  currentSortMode,
  collectionData
) {
  if (!isCollection) return false;

  const sortArray = currentSortMode === 'price' ? collectionData.byPrice : collectionData.byColor;
  const loadState =
    currentSortMode === 'price' ? collectionData.priceLoadState : collectionData.colorLoadState;

  if (!loadState.hasMore) return false;

  const bufferEndPage = currentLocation + bufferSize + 1;
  const bufferEndCardIndex = bufferEndPage * cardsPerPage;
  const checkStart = bufferEndCardIndex;
  const checkEnd = Math.min(checkStart + cardsPerPage, sortArray.length);

  for (let i = checkStart; i < checkEnd; i++) {
    if (sortArray[i]?.status === 'placeholder') {
      return true;
    }
  }
  return false;
}
